import type { JSX, ReactElement } from 'react';
import styled, { css } from 'styled-components';

import type { Props as BaseDialogProps } from 'components/ui/dialogs/Dialog';
import type { StructuredDialogButton } from 'components/ui/dialogs/StructuredDialog';

import MultiLineText from 'components/core/typography/MultiLineText';
import Text from 'components/core/typography/Text';
import { StructuredDialog } from 'components/ui/dialogs/StructuredDialog';
import { CheckIcon, ErrorIcon, WarningIcon } from 'components/ui/icons';
import { CircleContainer, Spinner } from 'components/ui/loading/Loader';
import BaseClass from 'components/ui/shared/BaseClass';
import { ElementTestId } from 'enums/testing';
import { translate } from 'utils/intlUtils';

import type { ApiError } from 'store/api/graph/interfaces/apiErrors';
import { getApiErrors, getApiErrorsMessages, logApiError } from 'store/api/graph/interfaces/apiErrors';

import { GREEN_600, RED_500, YELLOW_500 } from 'styles/tokens';
import { LINE_HEIGHT_DEFAULT } from 'styles/typography';

const { t } = translate;

/**
 * Icons that can be used in the PromptDialog message. `destructive` pairs with `isConfirmDestructive`.
 */
export type PromptDialogIcon = 'destructive' | 'warning';

const iconMap: { [key in PromptDialogIcon]: ReactElement<any> } = {
  destructive: <WarningIcon color={RED_500} />,
  warning: <WarningIcon color={YELLOW_500} />,
};

const ErrorList = styled.ul`
  list-style-type: none;
  padding: 0;
  text-align: center;
`;

const ErrorMessage = styled(Text)`
  line-height: ${LINE_HEIGHT_DEFAULT};
  white-space: pre-line;
`;

export interface PromptConfig {
  /**
   * Copy for the `Cancel` button
   */
  cancelText?: string;
  /**
   * Copy for the `Confirm` button
   */
  confirmText?: string;
  /**
   * Error override that bypasses the regular prompt to show a forced error
   */
  errorsOverride?: ApiError[];
  /**
   * If the dialogs confirm is a destructive action, the confirm button renders in red. The callbacks are
   * unchanged: onConfirm stays on the confirm button and onCancel on the cancel button.
   */
  isConfirmDestructive?: boolean;
  /**
   * Messaging shown when confirm/cancel dialog shows
   */
  message?: string;
  /**
   * Icon to show above the message
   */
  messageIcon?: PromptDialogIcon;
  /**
   * If a more dynamically styled message is needed, can supply a JSX Element block
   */
  messageJSX?: JSX.Element;
  /**
   * Callback for when the user clicks the `Cancel` button
   */
  onCancel?: () => void;
  /**
   * Reports whether confirmation succeeded. Cancellation reports false; confirmation without a handler reports true.
   */
  onComplete?: (success: boolean) => void;
  /**
   * Called on confirmation. An undefined or truthy result succeeds; a falsy result or rejection fails.
   */
  onConfirm?: () => Promise<unknown>;
  /**
   * Messaging shown when successful action
   */
  successMessage?: string;
  /**
   * Title in the Prompt Dialog box
   */
  title?: string;
}

export const DialogIconContainer = styled(CircleContainer)`
  position: fixed;
  transform: translate(-50%, -50%);
`;

interface Props extends BaseDialogProps, PromptConfig {
  errorLabel?: string;
}

interface State {
  errors?: ApiError[];
  isProcessing: boolean;
  isSuccess: boolean;
}

const initialState: State = { errors: undefined, isProcessing: false, isSuccess: false };

class PromptDialog extends BaseClass<Props, State> {
  static defaultProps = {
    get cancelText() {
      return t('cancel');
    },
    get confirmText() {
      return t('yes');
    },
    get errorLabel() {
      return t('okay');
    },
  };

  state = initialState;

  componentDidUpdate(propsPrev) {
    if (this.props.isOpen && !propsPrev.isOpen) {
      this.setState(initialState);
    }
  }

  getDialogButtons = (): StructuredDialogButton[] => {
    const { cancelText = '', confirmText = '', errorLabel = '', errorsOverride, isConfirmDestructive } = this.props;
    const { errors = errorsOverride, isProcessing, isSuccess } = this.state;

    if (isProcessing) {
      return [];
    }

    if (errors) {
      return [
        {
          label: errorLabel,
          onClick: this.onDialogCancel,
          variant: 'destructive' as const,
        },
      ];
    }

    if (isSuccess) {
      return [];
    }

    return [
      {
        label: cancelText,
        onClick: this.onDialogCancel,
        testId: ElementTestId.PROMPT_DIALOG_CANCEL,
        variant: 'secondary' as const,
      },
      {
        label: confirmText,
        onClick: () => void this.onDialogConfirm(),
        testId: ElementTestId.PROMPT_DIALOG_CONFIRM,
        variant: isConfirmDestructive ? ('destructive' as const) : ('primary' as const),
      },
    ];
  };

  getDialogContents = () => {
    const { errorsOverride, message, messageJSX, successMessage } = this.props;
    const { errors = errorsOverride, isProcessing, isSuccess } = this.state;

    if (isProcessing) {
      return <Spinner />;
    }

    if (errors) {
      const errorMessages = getApiErrorsMessages(errors);

      return (
        <ErrorList>
          {errorMessages.map(m => (
            <li key={m}>
              <ErrorMessage>{m}</ErrorMessage>
            </li>
          ))}
        </ErrorList>
      );
    }

    if (isSuccess) {
      return successMessage ? <MultiLineText>{successMessage}</MultiLineText> : null;
    }

    return (
      <>
        {message ? <MultiLineText>{message}</MultiLineText> : null}
        {messageJSX}
      </>
    );
  };

  /**
   * Returns the proper icon based on the state and given icon type.
   */
  getDialogIcon = () => {
    const { errorsOverride, messageIcon } = this.props;
    const { errors = errorsOverride, isSuccess } = this.state;

    if (errors) {
      return (
        <ErrorIcon
          color={RED_500}
          css={css`
            padding-bottom: 10px;
          `}
          height={72}
          width={72}
        />
      );
    }

    if (isSuccess) {
      return <CheckIcon color={GREEN_600} height={48} width={48} />;
    }

    if (messageIcon) {
      return iconMap[messageIcon];
    }

    return;
  };

  // TODO: (ED-15549) Delete Prompt - Don't display details error in prompt
  onActionCallback = async (action?: () => Promise<unknown>) => {
    const { onComplete, onConfirm } = this.props;

    this.setStateSynchronous({ isProcessing: true });

    if (action) {
      await action()
        .then((result: unknown) => this.setStateSynchronous({ isSuccess: result === undefined ? true : !!result }))
        .catch((error: Error) => {
          logApiError(error);
          this.setStateSynchronous({ errors: getApiErrors(error) });
        });
    }

    this.setStateSynchronous({ isProcessing: false });

    if (onComplete) {
      void onComplete(!action || (action === onConfirm && this.state.isSuccess));
    }
  };

  onDialogCancel = () => {
    const { onCancel } = this.props;
    void this.onActionCallback(async () => onCancel?.());
  };

  onDialogConfirm = async () => {
    const { onConfirm } = this.props;
    void this.onActionCallback(onConfirm);
  };

  render() {
    const { isDismissible, title } = this.props;
    const { isProcessing } = this.state;

    return (
      <StructuredDialog
        {...this.props}
        buttons={this.getDialogButtons()}
        dialogCss="max-width: 400px;"
        header={title}
        icon={this.getDialogIcon()}
        isDismissible={!isProcessing && isDismissible}
        onClose={this.onDialogCancel}
        testId={ElementTestId.PROMPT_DIALOG_CONTAINER}
      >
        {this.getDialogContents()}
      </StructuredDialog>
    );
  }
}

export default PromptDialog;
